import styled, { keyframes, css } from "styled-components";

/* ====== Settings ====== */
export const ACCENT = "#d4af37";

/* ====== Animations ====== */
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(14px) }
  to   { opacity: 1; transform: translateY(0) }
`;

const zoom = keyframes`
  from { transform: scale(1) }
  to   { transform: scale(1.04) }
`;

/* ====== Helpers ====== */
const elegantShadow = css`
  box-shadow: 0 8px 18px rgba(0, 0, 0, 0.06), 0 1px 0 rgba(0, 0, 0, 0.08) inset;
`;

/* ====== Section ====== */
export const Wrap = styled.section`
  --accent: ${(p) => p.$accent || ACCENT};
  background: transparent;
  color: #141414;
  padding: 6px 12px 6px 12px;
`;

export const Inner = styled.div`
  max-width: 1180px;
  margin: 0 auto;
`;

/* ====== Heading ====== */
export const Heading = styled.header`
  text-align: center;
  margin-bottom: clamp(20px, 3.6vw, 36px);

  h2 {
    margin: 0;
    font-size: clamp(24px, 4.2vw, 40px);
    line-height: 1.06;
    letter-spacing: 0.2px;
    font-family: "Georgia", "Times New Roman", serif;
  }

  .rule {
    display: inline-grid;
    grid-auto-flow: column;
    align-items: center;
    gap: 10px;
    margin-top: 12px;
  }

  .dot {
    width: 6px;
    height: 6px;
    border-radius: 999px;
    background: var(--accent);
  }

  .line {
    width: 84px;
    height: 1px;
    background: var(--accent);
    opacity: 0.85;
  }
`;

/* ====== Grid ====== */
export const Grid = styled.div`
  display: grid;
  gap: clamp(16px, 2.2vw, 24px);
  grid-template-columns: 1fr;

  @media (min-width: 720px) {
    grid-template-columns: 1fr 1fr;
  }
  @media (min-width: 1040px) {
    grid-template-columns: 1fr 1fr 1fr;
  }
`;

/* ====== Card ====== */
export const Card = styled.article`
  position: relative;
  border-radius: 18px;
  overflow: hidden;
  background: #0f0f0f;
  ${elegantShadow};

  border: 1px solid rgba(167, 134, 49, 0.18);
  outline: 1px solid rgba(20, 20, 20, 0.04);

  min-height: 360px;
  isolation: isolate;

  opacity: 0;
  &.is-visible {
    animation: ${fadeUp} 0.55s ease forwards;
  }

  transition: transform 0.2s ease, box-shadow 0.2s ease;
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 16px 36px rgba(0, 0, 0, 0.14);
  }
`;

export const Media = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transform: scale(1);
    transition: transform 0.45s ease;
    animation: ${zoom} 16s ease-in-out infinite alternate;
    filter: saturate(0.96) contrast(1.02);
  }

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(
      to top,
      rgba(0, 0, 0, 0.55) 0%,
      /* mai puțin negru la bază */ rgba(0, 0, 0, 0.35) 20%,
      /* rapid devine mai transparent */ rgba(0, 0, 0, 0.18) 40%,
      rgba(0, 0, 0, 0.06) 65%,
      transparent 85% /* mult mai repede dispare */
    );
    pointer-events: none;
    z-index: 1;
  }

  &::after {
    content: "";
    position: absolute;
    inset: -6% -6%;
    background: radial-gradient(
        120% 60% at 50% 100%,
        rgba(0, 0, 0, 0.48) 0%,
        rgba(0, 0, 0, 0.24) 45%,
        transparent 70%
      ),
      radial-gradient(
        100% 80% at 50% 0%,
        rgba(0, 0, 0, 0.06) 0%,
        transparent 60%
      );
    pointer-events: none;
    z-index: 2;
  }

  @media (pointer: fine) {
    ${Card}:hover & img {
      transform: scale(1.06);
    }
  }
`;

export const Badge = styled.div`
  position: absolute;
  top: 12px;
  left: 12px;
  display: inline-flex;
  align-items: center;
  gap: 8px;

  padding: 6px 10px;
  border-radius: 999px;
  font-size: 12px;
  letter-spacing: 0.5px;
  color: #fff;

  background: rgba(0, 0, 0, 0.35);
  border: 1px solid rgba(255, 255, 255, 0.18);
  backdrop-filter: blur(6px);
`;

export const Content = styled.div`
  position: absolute;
  inset: 0;
  padding: clamp(8px, 1.6vw, 16px) clamp(14px, 2.2vw, 22px)
    clamp(18px, 3vw, 28px);
  display: grid;
  align-content: end;
  gap: 6px;
  transform: translateY(4px);
  color: #fff;
  text-align: right; /* ✅ tot textul la dreapta */
`;

export const Title = styled.h3`
  margin: 0;
  font-family: "Georgia", "Times New Roman", serif;
  font-size: clamp(22px, 2.6vw, 32px);
  line-height: 1.04;
  text-shadow: 0 2px 12px rgba(0, 0, 0, 0.85), 0 1px 0 rgba(0, 0, 0, 0.4);
  text-align: right; /* ✅ */
`;

export const Desc = styled.p`
  opacity: 0.98;
  margin: 2px 0 0;
  font-size: 1rem;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.8), 0 1px 0 rgba(0, 0, 0, 0.35);
  text-align: right; /* ✅ */
`;

export const Eyebrow = styled.div`
  color: var(--accent);
  font-weight: 700;
  letter-spacing: 0.5px;
  font-size: 0.9rem;
  text-transform: uppercase;
  text-shadow: 0 1px 6px rgba(0, 0, 0, 0.65);
  text-align: right; /* ✅ */
`;

export const CTA = styled.a`
  justify-self: end; /* ✅ butonul la dreapta */
  margin-top: 6px;
  display: inline-grid;
  grid-auto-flow: column;
  align-items: center;
  gap: 10px;

  padding: 10px 14px;
  border-radius: 12px;
  color: #ffffffff;
  background: var(--accent);
  font-weight: 700;
  letter-spacing: 0.2px;
  text-decoration: none;
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.35);

  transition: filter 0.18s ease, transform 0.12s ease;
  &:hover {
    filter: brightness(1.06);
    transform: translateY(-1px);
  }
`;

export const Underline = styled.i`
  display: block;
  width: 74px;
  height: 2px;
  background: var(--accent);
  opacity: 0.9;
  border-radius: 2px;
  margin-left: auto; /* ✅ underline-ul merge sub textul din dreapta */
`;
